import React, { createContext, useState, useEffect, useCallback } from 'react';

export const ThemeContext = createContext(null);

const THEME_KEY = 'rodbot_theme';

export const ThemeProvider = ({ children }) => {
  const getInitialTheme = () => {
    const saved = localStorage.getItem(THEME_KEY);
    return ['light', 'dark', 'system'].includes(saved) ? saved : 'dark';
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [resolvedTheme, setResolvedTheme] = useState('dark');

  // Get system preference
  const getSystemTheme = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'dark';
  }, []);

  useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme;
    setResolvedTheme(resolved);
    document.documentElement.setAttribute('data-theme', resolved);
    document.documentElement.style.colorScheme = resolved;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = resolved === 'dark' ? '#1e293b' : '#f8fafc';
    }

    if (theme !== 'system' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const updateFromSystem = () => {
      const next = media.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      document.documentElement.setAttribute('data-theme', next);
      document.documentElement.style.colorScheme = next;
    };
    media.addEventListener?.('change', updateFromSystem);
    return () => media.removeEventListener?.('change', updateFromSystem);
  }, [theme, getSystemTheme]);

  const changeTheme = useCallback((newTheme) => {
    if (!['light', 'dark', 'system'].includes(newTheme)) return;
    setTheme(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
  }, []);

  const value = {
    theme,           // User's preference: 'light', 'dark', or 'system'
    resolvedTheme,   // Actual applied theme: 'light' or 'dark'
    changeTheme,
    isDark: resolvedTheme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

